/**
 * useResolvedDomain — load a practice's consent_form_config + return
 * URL builders for it.
 *
 * Why a hook on top of ``buildResolvedDomainUrl``:
 *   The 3 React-component callers (ConsentV2Link, ConsentV2LinkDialog,
 *   SendFormsPreview) all had identical scaffolding around the URL
 *   build: fetch the PracticeChoice row that holds the
 *   ``consent_form_config`` metadata, stash it in local state, build
 *   the URL when needed. This hook collapses that scaffolding into one
 *   place, with a module-level cache so re-opening the dialog for the
 *   same practice doesn't refetch.
 *
 * What it returns:
 *   {
 *     consentConfig,        // the raw row, in case the caller wants to
 *                           // inspect metadata directly (e.g. SendForms
 *                           // preview reads the `url_style`)
 *     isLoading,
 *     error,
 *     buildFormUrl(token, pathPrefix='consent'),
 *     buildDevUrl(token, pathPrefix='consent'),
 *   }
 *
 * Caching:
 *   The fetch is cached per practiceId for the lifetime of the page.
 *   When the super admin edits the practice's config on the Domain
 *   Management page the cache is NOT invalidated automatically — a
 *   subsequent send-flow would still see the stale value until the
 *   page reloads. Acceptable for Phase 1; PR5 will hook into the
 *   resolved-domain endpoint and Redux so updates propagate live.
 *
 * Localhost behaviour preserved:
 *   On localhost the production URL is still emitted from
 *   ``buildFormUrl`` (so the wizard's preview matches what the patient
 *   will see when sent from staging/prod). ``buildDevUrl`` returns the
 *   localhost equivalent so the developer can open it directly.
 */
import { useEffect, useMemo, useState } from 'react';
import { callApi } from 'src/utils/apiCall';
import {
    buildResolvedDomainUrl,
    buildDevDomainUrl,
} from 'src/utils/resolvedDomainUrl';

// Module-level cache: practiceId → { promise: Promise<consentConfig | null>, fetchedAt: number }.
// Multiple components mounting for the same practice share the in-flight
// request. The Promise resolves to ``null`` when no config row exists.
//
// Tech-debt fix (staging→main review): entries now carry a 5-minute TTL so
// surfaces that don't call invalidateResolvedDomain (ConsentV2Link,
// ConsentV2LinkDialog, SendFormsPreview) will eventually pick up domain
// config changes without a full page reload. invalidateResolvedDomain still
// works as an immediate-eviction path for the Domain Management save flow.
const _cache = new Map();
const _CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

function _fetchConsentConfig(practiceId) {
    const cached = _cache.get(practiceId);
    if (cached && Date.now() - cached.fetchedAt < _CACHE_TTL_MS) {
        return cached.promise;
    }
    const promise = callApi(
        `/api/v1/practices/${practiceId}/choices/?ordering=-created_at`
    )
        .then((res) => {
            if (res?.status === 200 && res.data?.results) {
                return (
                    res.data.results.find(
                        (c) => c?.metadata?.type === 'consent_form_config'
                    ) || null
                );
            }
            return null;
        })
        .catch(() => null);
    _cache.set(practiceId, { promise, fetchedAt: Date.now() });
    return promise;
}

/**
 * Drop the cached config for a practice. Call this from the Domain
 * Management page after a successful save so the next consent-send
 * picks up the new URL without a page reload.
 */
export function invalidateResolvedDomain(practiceId) {
    if (practiceId) _cache.delete(practiceId);
}

export default function useResolvedDomain(practiceId) {
    const [consentConfig, setConsentConfig] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        if (!practiceId) {
            setConsentConfig(null);
            return undefined;
        }
        setIsLoading(true);
        _fetchConsentConfig(practiceId)
            .then((cfg) => {
                if (cancelled) return;
                setConsentConfig(cfg);
                setIsLoading(false);
            })
            .catch((e) => {
                if (cancelled) return;
                setError(e);
                setIsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [practiceId]);

    const helpers = useMemo(() => {
        // Default pathPrefix is 'forms' to match the FORMS_V2 DomainUseCase
        // path_prefix seeded by apps/domains/migrations/0002. The previous
        // 'consent' default produced /consent/<token> links that 404'd on
        // any practice using the configured /forms/ path.
        const buildFormUrl = (token, pathPrefix = 'forms') =>
            buildResolvedDomainUrl({ consentConfig, pathPrefix, token });
        const buildDevUrl = (token, pathPrefix = 'forms') =>
            buildDevDomainUrl({ pathPrefix, token });
        return { buildFormUrl, buildDevUrl };
    }, [consentConfig]);

    return {
        consentConfig,
        isLoading,
        error,
        ...helpers,
    };
}
